import { useForm } from 'react-hook-form'
import { Form, useTransition } from '@remix-run/react'
import { useCallback } from 'react'
import type { getJob } from '~/models/job.server'
import type { Prisma } from '@prisma/client'
import type { HTMLInputTypeAttribute } from 'react'
import classnames from 'classnames'
import { UploadInput } from './UploadInput'
import { PhotoUploadInput } from './PhotoUploadInput'
import groupBy from 'lodash/groupBy'

import type { ErrorsList } from '~/types'
import { FieldType, Question, SelectChoice } from '~/types/apiTypes'

type FormField = {
  text: string
  name: string
  type: string
  required: boolean
  description?: string
  prompt?: string // for dropdown fields
  options?: { text: string }[]
  value?: number | string | null // can be a timestamp for Date field
}

type CustomQuestion = {
  id: string
  text: string
  fields: FormField[]
}

type OptionalField = {
  id: string
  type: string
  body: string
  required: boolean
  choices?: { id: string; body: string }[]
  single_answer?: boolean
  max_file_size?: number
  supported_file_types?: string[]
}

type SelectOption = {
  value: string
  label: string
}

type InputType = {
  type: HTMLInputTypeAttribute
  attrs: {
    [key: string]: string | number | boolean | SelectOption[]
  }
  customData?: {
    maxFileSize?: number
    supportedFileTypes?: string[]
  }
}

type RenderField = {
  name: string
  label: string
  type: InputType
  isCustom?: boolean
  isRequired?: boolean
}

function getInputType(field: FormField): InputType {
  const { type } = field

  if (type === 'string') {
    return {
      type: 'input',
      attrs: {
        type: 'text',
      },
    }
  }

  if (type === 'date') {
    return {
      type: 'input',
      attrs: {
        type: 'text',
        hint: 'YYYY-MM-DD',
      },
    }
  }

  if (type === 'multiple-choice') {
    return {
      type: 'checkboxes',
      attrs: {
        options: field.options!.map((choice) => ({
          value: choice.text,
          label: choice.text,
        })),
        singleAnswer: true,
      },
    }
  }

  if (type === 'numeric') {
    return {
      type: 'input',
      attrs: {
        type: 'number',
      },
    }
  }

  if (type === 'textarea') {
    return {
      type: 'textarea',
      attrs: {
        rows: 5,
      },
    }
  }

  if (type === 'file') {
    return {
      type: 'input',
      attrs: {
        type: 'file',
      },
      customData: {
        maxFileSize: 30 * 1024 * 1024,
        supportedFileTypes: ['pdf'],
      },
    }
  }

  if (type === 'boolean') {
    return {
      type: 'boolean',
      attrs: {
        options: [
          { value: 'true', label: 'Yes' },
          { value: 'false', label: 'No' },
        ],
      },
    }
  }

  if (type === 'dropdown') {
    return {
      type: 'select',
      attrs: {
        options: (field.options || []).map((choice) => ({
          label: choice.text,
          value: choice.text,
        })),
      },
    }
  }

  return {
    type: 'input',
    attrs: {
      type: 'text',
    },
  }
}

export const NewApplicationFormField = ({
  field,
  errors,
}: {
  field: Question
  errors: any
}) => {
  if (field.type === FieldType.SINGLE_SELECT && Array.isArray(field.choices)) {
    return (
      <div className="mt-8">
        <label className="block text-sm font-medium text-gray-700">
          {field.label}
          {field.required && <span className="text-rose-500"> *</span>}
        </label>
        {field.choices.map((option: SelectChoice, i) => (
          <div key={option.value} className="relative mt-4 flex items-start">
            <div className="flex h-5 items-center">
              <input
                id={`${field.id}-${option.value}`}
                name={field.id}
                value={option.value}
                type="radio"
                className={classnames(
                  'h-4 w-4 rounded-full border-gray-300 text-accent-color focus:ring-accent-color'
                )}
              />
            </div>
            <div className="ml-3 text-sm">
              <label
                htmlFor={`${field.id}-${option.value}`}
                className="font-normal text-gray-700"
              >
                {option.label}
              </label>
            </div>
          </div>
        ))}
        {errors && (
          <div className="pt-1 text-red-700" id="password-error">
            {errors[errors.length - 1]}
          </div>
        )}
      </div>
    )
  }

  if (field.type === FieldType.FILE) {
    const mappedField: RenderField = {
      name: field.id,
      label: field.label,
      type: {
        type: 'file',
        customData: {},
        attrs: {},
      },
      isRequired: field.required,
    }
    return <UploadInput field={mappedField} name={field.id} errors={errors} />
  }

  if (field.type === FieldType.TEXTAREA) {
    return (
      <div className="mt-8">
        {field.id !== 'additionalInformation' && (
          <label
            htmlFor="about"
            className="block text-sm font-medium text-gray-700"
          >
            {field.label}
            {field.required && <span className="text-rose-500"> *</span>}
          </label>
        )}
        <div className="mt-1">
          <textarea
            name={field.id}
            rows={3}
            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-gray-400 focus:ring-gray-400 sm:text-sm"
            placeholder={
              field.id === 'additionalInformation'
                ? 'Add a cover letter or anything else you want to share'
                : ''
            }
            defaultValue={''}
          />
        </div>
        {/* <p className="mt-2 text-sm text-gray-500">Max 1000 characters</p> */}
        {errors && (
          <div className="pt-1 text-red-700" id="password-error">
            {/* {errors[errors.length - 1]} */}
            {errors[errors.length - 1]}
          </div>
        )}
      </div>
    )
  }

  if (field.type === FieldType.TEXT) {
    return (
      <div className="mt-8">
        <label className="block text-sm font-medium text-gray-700">
          {field.label}
          {field.required && <span className="text-rose-500"> *</span>}
        </label>
        <div className="mt-1">
          <input
            // {...field.type.attrs}
            // @FIXME
            type={
              field.label.toLowerCase().includes('email') ? 'email' : 'text'
            }
            name={field.id}
            id={field.id}
            autoComplete="off"
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-400 focus:ring-gray-400 sm:text-sm"
          />
          {/* {field.type.attrs.hint && (
            <p className="mt-2 text-sm text-gray-500">
              {field.type.attrs.hint as string}
            </p>
          )} */}
          {errors && (
            <div className="pt-1 text-red-700" id="password-error">
              {errors[errors.length - 1]}
            </div>
          )}
        </div>
      </div>
    )
  }

  console.log('Skipping rendering field', field)

  return null
}

export const ApplicationFormField = ({
  field,
  errors,
}: {
  field: RenderField
  errors?: string[]
}) => {
  const fieldName = field.name

  if (
    field.type.attrs.type === 'file' &&
    field.label.toLowerCase() === 'photo'
  ) {
    return <PhotoUploadInput field={field} name={fieldName} errors={errors} />
  }

  if (
    field.type.attrs.type === 'file' &&
    field.label.toLowerCase() !== 'photo'
  ) {
    return <UploadInput field={field} name={fieldName} errors={errors} />
  }

  if (field.type.type === 'textarea') {
    return (
      <div className="mt-8">
        <label
          htmlFor="about"
          className="block text-sm font-medium text-gray-700"
        >
          {field.label}
          {field.isRequired && <span className="text-rose-500"> *</span>}
        </label>
        <div className="mt-1">
          <textarea
            name={fieldName}
            rows={3}
            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-gray-400 focus:ring-gray-400 sm:text-sm"
            placeholder=""
            defaultValue={''}
          />
        </div>
        {/* <p className="mt-2 text-sm text-gray-500">Max 1000 characters</p> */}
        {errors && (
          <div className="pt-1 text-red-700" id="password-error">
            {/* {errors[errors.length - 1]} */}
            {errors[errors.length - 1]}
          </div>
        )}
      </div>
    )
  }

  if (field.type.type === 'none') {
    return null
  }

  if (field.type.type === 'boolean') {
    const options = field.type.attrs.options as SelectOption[]

    return (
      <div className="mt-8">
        <label className="block text-sm font-medium text-gray-700">
          {field.label}
          {field.isRequired && <span className="text-rose-500"> *</span>}
        </label>
        <div className="mt-1">
          <select
            name={fieldName}
            className="mt-1 block rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-accent-color focus:outline-none focus:ring-accent-color sm:text-sm"
          >
            <option value="">- select -</option>
            {options.map((option: any) => (
              <option key={option.label} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors && (
            <div className="pt-1 text-red-700" id="password-error">
              {errors[errors.length - 1]}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (
    field.type.type === 'checkboxes' &&
    Array.isArray(field.type.attrs.options)
  ) {
    return (
      <div className="mt-8">
        <label className="block text-sm font-medium text-gray-700">
          {field.label}
          {field.isRequired && <span className="text-rose-500"> *</span>}
        </label>
        {field.type.attrs.options.map((option: SelectOption, i) => (
          <div key={option.value} className="relative mt-4 flex items-start">
            <div className="flex h-5 items-center">
              <input
                id={`${fieldName}-${option.value}`}
                name={`${fieldName}`}
                value={option.value}
                type={field.type.attrs.singleAnswer ? 'radio' : 'checkbox'}
                className={classnames(
                  'h-4 w-4 border-gray-300 text-accent-color focus:ring-accent-color',
                  field.type.attrs.singleAnswer ? 'rounded-full' : 'rounded'
                )}
              />
            </div>
            <div className="ml-3 text-sm">
              <label
                htmlFor={`${fieldName}-${option.value}`}
                className="font-normal text-gray-700"
              >
                {option.label}
              </label>
            </div>
          </div>
        ))}
        {errors && (
          <div className="pt-1 text-red-700" id="password-error">
            {errors[errors.length - 1]}
          </div>
        )}
      </div>
    )
  }

  if (field.type.type === 'select') {
    const options = field.type.attrs.options as SelectOption[]

    return (
      <div className="mt-8">
        <label className="block text-sm font-medium text-gray-700">
          {field.label}
          {field.isRequired && <span className="text-rose-500"> *</span>}
        </label>
        <div className="mt-1">
          <select
            name={fieldName}
            className="mt-1 block rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-accent-color focus:outline-none focus:ring-accent-color sm:text-sm"
          >
            <option value="">- select -</option>
            {options.map((option: any) => (
              <option key={option.label} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors && (
            <div className="pt-1 text-red-700" id="password-error">
              {errors[errors.length - 1]}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="mt-8">
      <label className="block text-sm font-medium text-gray-700">
        {field.label}
        {field.isRequired && <span className="text-rose-500"> *</span>}
      </label>
      <div className="mt-1">
        <input
          {...field.type.attrs}
          name={fieldName}
          id={field.name}
          autoComplete="off"
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-400 focus:ring-gray-400 sm:text-sm"
        />
        {field.type.attrs.hint && (
          <p className="mt-2 text-sm text-gray-500">
            {field.type.attrs.hint as string}
          </p>
        )}
        {errors && (
          <div className="pt-1 text-red-700" id="password-error">
            {errors[errors.length - 1]}
          </div>
        )}
      </div>
    </div>
  )
}

export const NewApplicationForm = ({
  job,
  formDefinition,
  errors,
}: {
  job: Awaited<ReturnType<typeof getJob>>
  formDefinition: { questions: Question[] }
  errors: ErrorsList
}) => {
  const transition = useTransition()

  const newSections = groupBy(
    formDefinition.questions.filter((question) => !!question.sectionName),
    'sectionName'
  )

  return (
    <div>
      <Form method="post" encType="multipart/form-data">
        {formDefinition.questions
          .filter((question) => !question.sectionName)
          .map((question, i) => {
            return (
              <NewApplicationFormField
                key={i}
                field={question}
                errors={errors ? errors[question.id] : undefined}
              />
            )
          })}
        {Object.entries(newSections).map(([section, fields]) => (
          <div key={section} className="mt-8 lg:mt-16">
            <h3 className="text-2xl font-medium text-gray-700">{section}</h3>
            {fields.map((question, i) => {
              return (
                <NewApplicationFormField
                  key={i}
                  field={question}
                  errors={errors ? errors[question.id] : undefined}
                />
              )
            })}
          </div>
        ))}
        <div className="pt-12">
          <div className="flex justify-center">
            <button
              type="submit"
              className="inline-flex justify-center rounded-md border border-transparent bg-accent-color py-3 px-8 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
            >
              {transition.state === 'submitting'
                ? 'Submitting...'
                : 'Submit application'}
            </button>
          </div>
        </div>
      </Form>
    </div>
  )
}
